/* eslint-disable react/jsx-props-no-spreading */
import React from "react";
import { v4 as uuidv4 } from "uuid";
import { EditComponentProps } from "material-table";
import { Field, getIn } from "formik";
import {
  IconButton,
  Paper,
  Typography,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Button,
  makeStyles,
  Theme,
  createStyles,
} from "@material-ui/core";
import AddBoxIcon from "@material-ui/icons/AddBox";
import CheckIcon from "@material-ui/icons/Check";
import DeleteIcon from "@material-ui/icons/Delete";
import { UserGroupData } from "../../../../types/config";
import { ConfigDialog } from "../../../../components";
import { UserGroupConfigContent } from "../../usergroup/UserGroupConfigContent";
import { UserGroupsApi } from "../../../../helpers";
import { OptionData } from "../../../../types";

/** スタイル定義 */
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    configDialogOpenButton: {
      marginTop: "1rem",
      color: theme.palette.text.secondary,
    },
  })
);

/** アイコンボタン（ラッパー） コンポーネント */
function UserGroupActionIconButton(props: { children: React.ReactNode }) {
  const { children } = props;

  //--------------------
  // コンポーネント
  return (
    <div
      style={{
        marginLeft: "1rem",
        marginTop: "0.125rem",
        cursor: "pointer",
      }}
    >
      {children}
    </div>
  );
}

interface Props {
  open: boolean;
  onClickOpen: () => void;
  onClose: () => void;
  onError: (error: any) => void;
}

/** ユーザーグループダイアログコンポーネント */
function UserGroupDialogComponent(props: Props) {
  const classes = useStyles();

  const { open, onClickOpen, onClose, onError } = props;

  //--------------------
  // コンポーネント
  return (
    <div style={{ marginTop: "0.5rem" }}>
      <Button
        className={classes.configDialogOpenButton}
        size="small"
        onClick={onClickOpen}
        // onClick={() => {
        //   setOpen(true);
        // }}
      >
        ユーザーグループ設定
      </Button>
      <ConfigDialog
        open={open}
        title="ユーザーグループ設定"
        okLabel="閉じる"
        // handleClose={() => {
        //   setOpen(false);
        // }}
        handleClose={onClose}
      >
        <div style={{ minWidth: 800 }}>
          <UserGroupConfigContent onError={onError} />
        </div>
      </ConfigDialog>
    </div>
  );
}

interface AddComponentProps {
  onClick: () => void;
  disabled: boolean;
}

/** 選択肢追加コンポーネント */
function AddComponent(props: AddComponentProps) {
  const { onClick, disabled } = props;

  //--------------------
  // コンポーネント
  return (
    <div>
      <IconButton aria-label="add" onClick={onClick} disabled={disabled}>
        <AddBoxIcon />
      </IconButton>
    </div>
  );
}

/**
 * ユーザーグループ選択肢において、
 * ユーザーグループIDからユーザーグループ名称を検索する
 */
const findName = (
  id: string | number | undefined,
  userGroupMasterDataList: OptionData[]
): string => {
  let name = "";
  let idStr = "";
  if (typeof id === "string") {
    idStr = id;
  } else if (typeof id === "number") {
    idStr = String(id);
  }
  const matched = userGroupMasterDataList.find(
    (userGroup: OptionData) => userGroup.value === idStr
  );
  if (matched) {
    name = matched.name;
  }
  return name;
};

/** 未確定のユーザーグループ選択肢を取得する */
function getUnselectedOptions(
  options: OptionData[],
  userGroups: UserGroupData[]
): OptionData[] {
  return options.filter((option: OptionData) => {
    return !userGroups.find((userGroup: UserGroupData) => {
      return userGroup.confirmed && userGroup.selectedId === option.value;
    });
  });
}

/** ユーザーグループ選択肢を追加できるか？ */
function isAddable(
  remainOptions: OptionData[],
  unconfirmedUserGroups: UserGroupData[]
): boolean {
  let addable = true;
  if (remainOptions.length > 0 && unconfirmedUserGroups.length > 0) {
    // 未選択の選択肢があるが、
    // 選択操作中の場合は追加ボタンを押せない
    addable = false;
  } else if (remainOptions.length === 0) {
    // 未選択の選択肢が残っていない場合は追加ボタンを押せない
    addable = false;
  }
  return addable;
}

interface UserGroupEditComponentProps<T extends object>
  extends EditComponentProps<T> {
  onError: (error: any) => void;
}

/** ユーザーグループ編集コンポーネント */
export function UserGroupEditComponent<T extends object>(
  props: UserGroupEditComponentProps<T>
) {
  const { onError } = props;

  //------------------------------
  // ユーザーグループ
  // モーダル
  const [open, setOpen] = React.useState<boolean>(false);
  // 選択肢
  const [userGroupOptions, setUserGroupOptions] = React.useState<OptionData[]>(
    []
  );

  // ユーザーグループ選択肢をAPIで再取得して更新する
  const updateUserGroupOptions = (isCleanedUp: boolean) => {
    const fetchData = async () => {
      try {
        const res: any = await UserGroupsApi.list();
        const newUserGroupOptions = res.data.map((row: any) => ({
          name: row.name,
          value: String(row.id),
        }));
        if (!isCleanedUp) {
          // クリーンアップされてなければStateを更新する
          setUserGroupOptions(newUserGroupOptions);
        }
      } catch (error: any) {
        onError(error);
      }
    };
    fetchData();
  };

  // 初回のみ、ユーザーグループ選択肢を更新する
  React.useEffect(() => {
    let cleanedUp = false;

    updateUserGroupOptions(cleanedUp);

    return function cleanup() {
      cleanedUp = true;
    };
  }, []);

  // ダイアログを閉じたとき、
  // ユーザーグループ選択肢を最新状態に更新する
  React.useEffect(() => {
    let cleanedUp = false;

    if (!open) {
      updateUserGroupOptions(cleanedUp);
    }

    return function cleanup() {
      cleanedUp = true;
    };
  }, [open]);

  //--------------------
  // コンポーネント
  return (
    // eslint-disable-next-line react/destructuring-assignment
    <Field name={props.columnDef.field}>
      {(args: any) => {
        const { field, form } = args;
        const { name } = field;
        const { errors, touched, setFieldValue } = form;
        const showError = !!getIn(errors, name);

        // 未確定のユーザーグループ
        let unconfirmedUserGroups: UserGroupData[] = [];
        // 未選択の選択肢
        let remainOptions = userGroupOptions;

        // ユーザーにユーザーグループが紐づいているとき、
        // 未確定のユーザーグループ、未選択の選択肢を取得する
        if (props.value) {
          unconfirmedUserGroups = props.value.filter((value: UserGroupData) => {
            return !value.confirmed;
          });
          remainOptions = getUnselectedOptions(userGroupOptions, props.value);
        }

        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex" }}>
              <AddComponent
                onClick={() => {
                  // 以下の場合にプルダウンを追加する
                  // ・選択中のプルダウンが無い
                  // ・未選択のユーザーグループが残っている
                  if (unconfirmedUserGroups.length === 0) {
                    // ユーザーグループ枠を追加する
                    const newValue = [
                      ...props.value,
                      {
                        uuid: uuidv4(),
                        selectedId: "",
                        selectedName: "",
                        confirmed: false,
                      },
                    ];
                    props.onChange(newValue); // for MaterialTable
                    setFieldValue(name, newValue); // for Formik
                  }
                }}
                disabled={!isAddable(remainOptions, unconfirmedUserGroups)}
              />
              <div>
                {props.value &&
                  props.value.map(
                    (userGroupValue: UserGroupData, index: number) => {
                      // 選択肢単位
                      return (
                        <Paper
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            marginTop: "0.25rem",
                          }}
                          key={userGroupValue.uuid}
                        >
                          <div
                            style={{
                              display: "flex",
                              padding: "0.25rem 0.5rem 0.25rem 0.5rem",
                            }}
                          >
                            {userGroupValue.confirmed ? (
                              <Typography
                                component="span"
                                style={{
                                  fontSize: "0.875rem",
                                  marginTop: "0.4rem",
                                }}
                              >
                                {userGroupValue.selectedName}
                              </Typography>
                            ) : (
                              <FormControl
                                style={{
                                  minWidth: "5rem",
                                  marginLeft: "0.5rem",
                                  fontSize: "0.875rem",
                                }}
                              >
                                <Select
                                  {...props}
                                  {...field}
                                  value={userGroupValue.selectedId || ""}
                                  style={{ fontSize: "0.875rem" }}
                                  onChange={(
                                    event: React.ChangeEvent<{
                                      value: unknown;
                                    }>
                                  ) => {
                                    const selectedId = event.target
                                      .value as string;

                                    // 値更新
                                    const newValue = [...props.value];
                                    newValue[index] = {
                                      ...userGroupValue,
                                      selectedId,
                                      selectedName: findName(
                                        selectedId,
                                        userGroupOptions
                                      ),
                                    };
                                    props.onChange(newValue); // for MaterialTable
                                    setFieldValue(name, newValue); // for Formik
                                  }}
                                  error={showError}
                                  disabled={userGroupValue.confirmed}
                                >
                                  {remainOptions.map(
                                    (userGroupMaster: OptionData) => {
                                      return (
                                        <MenuItem
                                          dense
                                          value={userGroupMaster.value}
                                          key={userGroupMaster.value}
                                        >
                                          {userGroupMaster.name}
                                        </MenuItem>
                                      );
                                    }
                                  )}
                                </Select>
                                <FormHelperText>
                                  {errors[name] &&
                                    touched[name] &&
                                    errors[name]}
                                </FormHelperText>
                              </FormControl>
                            )}
                            {userGroupValue.confirmed ? (
                              // 削除ボタン
                              <UserGroupActionIconButton>
                                <DeleteIcon
                                  aria-label="delete"
                                  onClick={() => {
                                    const newValue = [...props.value];
                                    newValue.splice(index, 1);
                                    props.onChange(newValue); // for MaterialTable
                                    setFieldValue(name, newValue); // for Formik
                                  }}
                                />
                              </UserGroupActionIconButton>
                            ) : (
                              // 確定ボタン
                              <UserGroupActionIconButton>
                                <CheckIcon
                                  aria-label="check"
                                  onClick={() => {
                                    if (props.value[index].selectedId) {
                                      const newValue = [...props.value];
                                      newValue[index] = {
                                        ...userGroupValue,
                                        confirmed: true,
                                      };
                                      props.onChange(newValue); // for MaterialTable
                                      setFieldValue(name, newValue); // for Formik
                                    }
                                  }}
                                />
                              </UserGroupActionIconButton>
                            )}
                          </div>
                        </Paper>
                      );
                    }
                  )}
              </div>
            </div>
            <UserGroupDialogComponent
              open={open}
              onClickOpen={() => {
                setOpen(true);
              }}
              onClose={() => {
                setOpen(false);
              }}
              onError={onError}
            />
          </div>
        );
      }}
    </Field>
  );
}
